import { HilModuleResolutionType } from '@cadstrom/backend/shared';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import StorefrontIcon from '@mui/icons-material/Storefront';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import { useQuery } from '@tanstack/react-query';
import { useMatchRoute, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { Suspense, useMemo, useState } from 'react';

import { useCoreModelContext } from '#components/cad-renderer/context/core-model-context.js';
import { useDatasheetPopoverStore } from '#components/datasheet-popover/datasheet-popover-store.js';
import { usePortalDocument } from '#components/datasheet-popover/portal-document-context.js';
import { DnpBadge } from '#components/dnp-badge.js';
import { Loading } from '#components/loading.js';
import { MenuItemLink } from '#components/router-links/menu-item-link.js';
import { useTRPC } from '#src/trpc.js';
import { Can } from '#state/contexts/ability-context.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { useIsModuleDnp } from '#utils/use-is-module-dnp.js';
import { useLatestRevisionId } from '#utils/use-latest-revision-id.js';
import { deriveModulePinoutRows } from './get-module-pinout-rows.js';
import { ModuleCardOverviewTab } from './module-card-overview-tab.js';
import { ModuleCardPinoutTab } from './module-card-pinout-tab.js';

import type { ProjectModule } from '@cadstrom/sigma-engine-shared';
import type { Promisable } from 'type-fest';

const detailTabs = [
	{ id: 'overview', label: 'Overview' },
	{ id: 'pinout', label: 'Pinout' },
] as const;

type DetailTabId = (typeof detailTabs)[number]['id'];

export interface ModelModuleChipContextMenuProps {
	/** The name of the specific placed instance targeted by zoom actions (may differ from the project-level reference designator for sub-schematic instances). */
	moduleInstanceName: string;
	projectModule: ProjectModule | undefined;
	/** Called when the user triggers a zoom-to-item action; the callback also closes the menu. */
	onZoomToItem: (referenceDesignator: string) => Promisable<void>;
	/** Called when the menu should close (e.g. after a navigation action). */
	onClose: () => void;
	/** Additional menu items to render; receives the onClose callback. */
	renderAdditionalMenuItems?: (closeMenu: () => void) => React.ReactNode;
	/** The schematic instances relevant to the current pin/pad/signal context. */
	relevantInstances: Array<{ name: string }>;
}

export function ModelModuleChipContextMenu({
	moduleInstanceName,
	projectModule,
	onZoomToItem,
	onClose,
	renderAdditionalMenuItems,
	relevantInstances,
}: ModelModuleChipContextMenuProps) {
	const { coreModel } = useCoreModelContext(false) ?? {};
	const openDatasheetPopover = useDatasheetPopoverStore((state) => state.open);
	const portalDocument = usePortalDocument();
	const trpc = useTRPC();
	const matchRoute = useMatchRoute();

	const params = useParams({ strict: false });
	const projectId = 'projectId' in params ? (params.projectId ?? null) : null;
	const orgName = 'orgName' in params ? (params.orgName ?? null) : null;
	const buildId = 'revisionId' in params ? (params.revisionId ?? undefined) : undefined;
	const latestRevisionId = useLatestRevisionId(projectId);
	const revisionId = buildId ?? latestRevisionId ?? null;

	const isOnAnalysisPage = Boolean(
		matchRoute({ to: '/orgs/$orgName/projects/$projectId/revisions/$revisionId/results' }),
	);

	const projectReferenceDesignator = projectModule?.name ?? moduleInstanceName;

	const {
		data: moduleResolutionData,
		isLoading: isModuleResolutionLoading,
		isError: isModuleResolutionError,
	} = useQuery(
		trpc.moduleResolution.get.queryOptions(
			{
				projectId: projectId!,
				referenceDesignator: projectReferenceDesignator,
				buildId,
			},
			{
				enabled: projectId !== null,
				throwOnError: false,
			},
		),
	);
	const moduleResolution = moduleResolutionData?.moduleResolution ?? null;
	const isPackageStoreResolution = moduleResolution?.resolutionType === HilModuleResolutionType.PackageStore;

	// Falls back to the parsed flag when no resolution exists.
	const isDnp = useIsModuleDnp(projectReferenceDesignator, projectModule);

	// Sigma-model resolutions have no package-store part, so the lookup is skipped for them. It is
	// still attempted when the resolution lookup itself failed, since treating an unknown resolution
	// type as "no package" would silently drop the datasheet on an unrelated failure.
	const shouldFetchPackage = isPackageStoreResolution || isModuleResolutionError;

	const { data: packageData, isLoading: isPackageDataLoading } = useQuery(
		trpc.project.getPackageByRefDesignator.queryOptions(
			{
				projectId: projectId!,
				buildId,
				referenceDesignator: projectReferenceDesignator,
			},
			{
				enabled: projectId !== null && shouldFetchPackage,
				throwOnError: false,
			},
		),
	);
	const pkg = packageData?.package ?? null;
	const manufacturerName = packageData?.manufacturerName ?? null;
	const isConfidential = packageData?.isConfidential ?? false;
	const isPackageLoading = isModuleResolutionLoading || (shouldFetchPackage && isPackageDataLoading);

	const [expandedTab, setExpandedTab] = useState<DetailTabId | null>(null);

	const pinoutRows = useMemo(
		() => deriveModulePinoutRows({ coreModel, projectModule, moduleResolution, pkg }),
		[coreModel, projectModule, moduleResolution, pkg],
	);

	const datasheetFileId = pkg?.fileId ?? null;

	// Opening the datasheet dismisses this card (only one datasheet popover is shown at a time).
	const handleOpenDatasheet = (fileId: string, ownerDocument: Document | null) => {
		if (!pkg) {
			return;
		}
		openDatasheetPopover(
			{
				fileId,
				pkgId: pkg.id,
				mpn: pkg.manufacturerPartNumber,
				datasheetUrl: pkg.datasheetUrl,
				manufacturerName,
				referenceDesignator: projectReferenceDesignator,
				projectId,
			},
			ownerDocument ?? portalDocument,
		);
		onClose();
	};

	const el = useDataElTagGenerator(ModelModuleChipContextMenu);
	return (
		<div {...el()} className='flex max-h-[80dvh] min-h-0 w-100 flex-col'>
			<div {...el('moduleCard')} className='flex min-h-0 flex-col gap-2 border-b border-gray-a4 p-3'>
				<div {...el('moduleCardHeader')} className='shrink-0'>
					{datasheetFileId !== null && (
						<Button
							variant='outlined'
							startIcon={<PictureAsPdfIcon />}
							onClick={(event) => handleOpenDatasheet(datasheetFileId, event.currentTarget.ownerDocument)}
							className='float-right mb-2 ml-2'
						>
							<span>Datasheet</span>
						</Button>
					)}

					<div {...el('moduleCardHeaderTitle')} className='flex items-start justify-between gap-3 py-0.5'>
						<div className='flex min-w-0 items-baseline gap-2'>
							<span className='font-mono leading-tight font-bold'>{projectReferenceDesignator}</span>
							{isDnp && <DnpBadge className='shrink-0 self-center' />}
							{isPackageLoading ?
								<Skeleton width={96} />
							:	pkg && (
									<span className='truncate -my-1 py-1 text-gray-a11' title={pkg.manufacturerPartNumber}>
										{pkg.manufacturerPartNumber}
									</span>
								)
							}
						</div>
					</div>
					<div {...el('moduleCardHeaderSubtitle')} className='mt-2 flex flex-wrap items-baseline gap-2'>
						{isPackageLoading ?
							<Skeleton width={140} />
						:	<>
								{(() => {
									if (pkg) {
										return (
											<p className='truncate -my-1 py-1 text-gray-a9'>
												{manufacturerName} · {pkg.sigmaModelType}
											</p>
										);
									}
									const sigmaModelType =
										moduleResolution?.sigmaModelResolutionProperties.sigmaModelType ?? projectModule?.model?.modelId;
									if (sigmaModelType !== undefined) {
										return (
											<p className='truncate -my-1 py-1 text-gray-a9'>
												{sigmaModelType}
												{projectModule?.value ? `, ${projectModule.value}` : ''}
											</p>
										);
									}
									return null;
								})()}

								{isConfidential && (
									<Tooltip title='This part is from a confidential, customer-owned package family'>
										<Chip variant='outlined' color='primary' label='Confidential' />
									</Tooltip>
								)}
							</>
						}
					</div>
				</div>

				<div className='flex shrink-0 gap-2'>
					{detailTabs.map((tab) => (
						<Button
							key={tab.id}
							variant='text'
							endIcon={
								<ExpandMoreIcon className={clsx('transition-transform', expandedTab !== tab.id && '-rotate-90')} />
							}
							className={clsx(
								'm-0 border border-gray-a3 bg-gray-a4 px-1.5 py-1 text-gray-11',
								expandedTab === tab.id && 'bg-gray-a6 text-gray-12',
							)}
							onClick={() => setExpandedTab((current) => (current === tab.id ? null : tab.id))}
						>
							<span>{tab.label}</span>
						</Button>
					))}
				</div>
				<div className='-m-3 mt-0 flex min-h-0 flex-col overflow-y-auto'>
					{expandedTab !== null && (
						<div className='flex min-h-0 flex-col'>
							<Suspense fallback={<Loading />}>
								{expandedTab === 'overview' && !isPackageLoading && (
									<ModuleCardOverviewTab pkg={pkg} projectModule={projectModule} />
								)}
								{expandedTab === 'pinout' && <ModuleCardPinoutTab rows={pinoutRows} />}
							</Suspense>
						</div>
					)}
				</div>
			</div>

			<MenuList className='max-h-[30%] shrink-0 overflow-auto'>
				<MenuItem onClick={() => void onZoomToItem(moduleInstanceName)}>
					<ListItemIcon>
						<CenterFocusStrongIcon />
					</ListItemIcon>
					<ListItemText>Zoom to item</ListItemText>
				</MenuItem>

				{projectId !== null && orgName !== null && revisionId !== null && (
					<Can I='manage' a='moduleResolution'>
						<MenuItemLink
							to='/orgs/$orgName/projects/$projectId/revisions/$revisionId/resolution-editor'
							params={{ orgName, projectId, revisionId }}
							search={{
								modules: [projectReferenceDesignator],
								// The Resolution Editor table remembers its state (filters, pagination, etc.) in local storage.
								// But here, we're linking to a specific module, so having those filters enabled might hide the module we linked to.
								// So we tell the Resolution Editor to ignore any persisted table filters.
								ignoreFilters: isOnAnalysisPage ? 'true' : undefined,
							}}
							target='_blank'
							onClick={onClose}
						>
							<ListItemIcon>
								<OpenInNewIcon />
							</ListItemIcon>
							<ListItemText>Open in Resolution Editor</ListItemText>
						</MenuItemLink>
					</Can>
				)}

				<Can I='manage' a='package'>
					{(() => {
						const text = 'Open in Package Store';
						if (isPackageLoading) {
							return (
								<MenuItem disabled>
									<ListItemIcon>
										<CircularProgress size={12} />
									</ListItemIcon>
									<ListItemText>{text}</ListItemText>
								</MenuItem>
							);
						} else if (pkg) {
							return (
								<MenuItemLink
									to='/package-store/$packageFamilyId/packages/$packageId'
									params={{ packageFamilyId: pkg.pkgFamilyId, packageId: pkg.id }}
									target='_blank'
									onClick={onClose}
								>
									<ListItemIcon>
										<StorefrontIcon />
									</ListItemIcon>
									<ListItemText>{text}</ListItemText>
								</MenuItemLink>
							);
						}
					})()}
				</Can>

				{renderAdditionalMenuItems?.(onClose)}

				{relevantInstances.length > 1 && (
					<MenuList className='max-h-40 overflow-auto'>
						<p className='sticky top-0 z-10 bg-gray-4 p-2 text-lg font-semibold'>Instances:</p>
						{relevantInstances.map((instance) => (
							<MenuItem key={instance.name} onClick={() => void onZoomToItem(instance.name)}>
								<ListItemText className='ml-2 italic'>{instance.name}</ListItemText>
							</MenuItem>
						))}
					</MenuList>
				)}
			</MenuList>
		</div>
	);
}
